import { db } from '../auth/firebaseConfig';
import {
  doc, setDoc, getDoc, updateDoc, deleteDoc,
  collection, getDocs, addDoc, query, where, orderBy, limit,
  serverTimestamp, onSnapshot
} from 'firebase/firestore';

const GROUPS_COLLECTION = 'ubunited_groups';
const DM_COLLECTION = 'ubunited_dms';
const READ_RECEIPTS_COLLECTION = 'ubunited_read_receipts';
const GROUP_MEMBERS_SUBCOLLECTION = 'members';

const CORE_GROUP_ID = 'ubunited-core';

// --- Core Group Bootstrap ---

export async function ensureCoreGroup(userId) {
  const coreRef = doc(db, GROUPS_COLLECTION, CORE_GROUP_ID);
  const coreSnap = await getDoc(coreRef);

  if (!coreSnap.exists()) {
    await setDoc(coreRef, {
      name: 'The Ubuniverse',
      description: 'The default group for all UBunited members.',
      visibility: 'public',
      isDefault: true,
      createdBy: 'system',
      createdAt: new Date().toISOString(),
      memberCount: 0,
    });
  }

  const memberRef = doc(db, GROUPS_COLLECTION, CORE_GROUP_ID, GROUP_MEMBERS_SUBCOLLECTION, userId);
  const memberSnap = await getDoc(memberRef);
  if (!memberSnap.exists()) {
    await setDoc(memberRef, {
      joinedAt: new Date().toISOString(),
      role: 'member',
    });
  }
}

// --- User Search (for DMs) ---

// Member autocomplete: names only — emails are NEVER searched or returned.
// The member list is cached for 5 minutes so typing feels instant.
let memberCache = null;
let memberCacheTime = 0;

export async function searchUsers(searchTerm) {
  if (!memberCache || Date.now() - memberCacheTime > 5 * 60 * 1000) {
    const q = query(collection(db, 'users'), where('signupSite', '==', 'ubunited'));
    const snapshot = await getDocs(q);
    memberCache = snapshot.docs
      .map(d => {
        const u = d.data();
        return { id: d.id, firstName: u.firstName || '', lastName: u.lastName || '' };
      })
      .filter(u => u.firstName || u.lastName); // no name = not findable
    memberCacheTime = Date.now();
  }
  const term = searchTerm.toLowerCase();
  return memberCache
    .filter(u =>
      u.firstName.toLowerCase().startsWith(term) ||
      u.lastName.toLowerCase().startsWith(term) ||
      `${u.firstName} ${u.lastName}`.trim().toLowerCase().includes(term)
    )
    .slice(0, 8);
}

// --- DM Conversations List ---

export async function getDmConversations(userId) {
  const q = query(collection(db, 'ubunited_dm_index'), where('participants', 'array-contains', userId));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function ensureDmIndex(fromUserId, fromUserName, toUserId, toUserName) {
  const channelId = getDmChannelId(fromUserId, toUserId);
  const indexRef = doc(db, 'ubunited_dm_index', channelId);
  // Write straight through with merge instead of read-then-create. The read was
  // the bug: security rules gate this doc on `uid in resource.data.participants`,
  // and `resource` is null for a doc that doesn't exist yet, so the get was
  // denied for every first message — i.e. every conversation. A merged set is a
  // `create` when absent and an `update` when present, and the sender is always
  // a participant, so both branches pass. lastMessage is deliberately omitted so
  // an existing conversation's preview survives; updateDmIndex sets it next.
  await setDoc(indexRef, {
    participants: [fromUserId, toUserId],
    participantNames: { [fromUserId]: fromUserName, [toUserId]: toUserName },
  }, { merge: true });
  return channelId;
}

export async function updateDmIndex(channelId, lastMessage) {
  await updateDoc(doc(db, 'ubunited_dm_index', channelId), {
    lastMessage,
    lastMessageAt: new Date().toISOString(),
  });
}

// --- User's Groups (with recent activity) ---

export async function getUserGroupsWithActivity(userId, maxCount = 3) {
  const allGroups = await getGroups();
  const myGroups = [];

  for (const group of allGroups) {
    const memberSnap = await getDoc(
      doc(db, GROUPS_COLLECTION, group.id, GROUP_MEMBERS_SUBCOLLECTION, userId)
    );
    if (memberSnap.exists()) {
      // Get the most recent message timestamp
      const msgQuery = query(
        collection(db, GROUPS_COLLECTION, group.id, 'messages'),
        orderBy('createdAt', 'desc'),
        limit(1)
      );
      const msgSnap = await getDocs(msgQuery);
      const lastActivity = msgSnap.empty
        ? group.createdAt
        : msgSnap.docs[0].data().createdAt;

      const lastMsg = msgSnap.empty ? null : msgSnap.docs[0].data().message;

      myGroups.push({ ...group, lastActivity, lastMessage: lastMsg });
    }
  }

  myGroups.sort((a, b) => (b.lastActivity || '').localeCompare(a.lastActivity || ''));
  return myGroups.slice(0, maxCount);
}

// --- Group Management ---

export async function createGroup(userId, groupData) {
  const docRef = doc(collection(db, GROUPS_COLLECTION));
  await setDoc(docRef, {
    ...groupData,
    createdBy: userId,
    createdAt: new Date().toISOString(),
    memberCount: 1,
  });
  await setDoc(doc(db, GROUPS_COLLECTION, docRef.id, GROUP_MEMBERS_SUBCOLLECTION, userId), {
    joinedAt: new Date().toISOString(),
    role: 'owner',
  });
  return docRef.id;
}

export async function getGroups() {
  const q = query(collection(db, GROUPS_COLLECTION), orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function getGroup(groupId) {
  const docSnap = await getDoc(doc(db, GROUPS_COLLECTION, groupId));
  return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
}

export async function joinGroup(groupId, userId) {
  await setDoc(doc(db, GROUPS_COLLECTION, groupId, GROUP_MEMBERS_SUBCOLLECTION, userId), {
    joinedAt: new Date().toISOString(),
    role: 'member',
  });
}

export async function leaveGroup(groupId, userId) {
  await deleteDoc(doc(db, GROUPS_COLLECTION, groupId, GROUP_MEMBERS_SUBCOLLECTION, userId));
}

// Owner removes someone else. Goes through a Cloud Function so ownership is
// verified server-side (the members subcollection is writable by any member).
export async function removeGroupMember(groupId, userId) {
  const { getFunctions, httpsCallable } = await import('firebase/functions');
  const { default: app } = await import('../auth/firebaseConfig');
  const fn = httpsCallable(getFunctions(app, 'us-east1'), 'ubuRemoveGroupMember');
  await fn({ groupId, userId });
}

// Add an existing member by uid and email them a heads-up. Server-side so the
// member's email address never has to be exposed to the client.
export async function addGroupMember(groupId, userId) {
  const { getFunctions, httpsCallable } = await import('firebase/functions');
  const { default: app } = await import('../auth/firebaseConfig');
  const fn = httpsCallable(getFunctions(app, 'us-east1'), 'ubuAddGroupMember');
  const res = await fn({ groupId, userId });
  return res.data;
}

// Who may invite people in: private groups are owner-only, public groups are
// open to any member. Mirrors assertCanInvite() in the Cloud Function.
export function canInviteToGroup(group, userId, isMember) {
  if (!group || !userId) return false;
  // The Ubuniverse is the site-wide default everyone is already in
  if (group.isDefault) return false;
  if (group.createdBy === userId) return true;
  if (group.visibility === 'private') return false;
  return !!isMember;
}

export async function isGroupMember(groupId, userId) {
  const docSnap = await getDoc(
    doc(db, GROUPS_COLLECTION, groupId, GROUP_MEMBERS_SUBCOLLECTION, userId)
  );
  return docSnap.exists();
}

// --- Group Messages ---

export async function sendGroupMessage(groupId, userId, userName, message) {
  await addDoc(collection(db, GROUPS_COLLECTION, groupId, 'messages'), {
    userId,
    userName,
    message,
    createdAt: new Date().toISOString(),
  });
}

export async function getGroupMessages(groupId, messageLimit = 50) {
  const q = query(
    collection(db, GROUPS_COLLECTION, groupId, 'messages'),
    orderBy('createdAt', 'desc'),
    limit(messageLimit)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() })).reverse();
}

export function subscribeToGroupMessages(groupId, callback, messageLimit = 50) {
  const q = query(
    collection(db, GROUPS_COLLECTION, groupId, 'messages'),
    orderBy('createdAt', 'desc'),
    limit(messageLimit)
  );
  return onSnapshot(q, (snapshot) => {
    const messages = snapshot.docs.map(d => ({ id: d.id, ...d.data() })).reverse();
    callback(messages);
  });
}

// --- Direct Messages ---

function getDmChannelId(userId1, userId2) {
  return [userId1, userId2].sort().join('_');
}

export async function sendDirectMessage(fromUserId, fromUserName, toUserId, message) {
  // Safety guards: blocks and minor-adult restriction
  const { isBlockedBetween, canDirectMessage } = await import('./safetyService');
  const blocked = await isBlockedBetween(fromUserId, toUserId);
  if (blocked) {
    throw new Error('You cannot message this user.');
  }
  const dmCheck = await canDirectMessage(fromUserId, toUserId);
  if (!dmCheck.allowed) {
    throw new Error(dmCheck.reason);
  }

  const channelId = getDmChannelId(fromUserId, toUserId);
  await addDoc(collection(db, DM_COLLECTION, channelId, 'messages'), {
    fromUserId,
    fromUserName,
    toUserId,
    message,
    createdAt: new Date().toISOString(),
    read: false,
  });
}

// Soft-delete: message text is replaced, record kept for moderation/legal purposes
export async function deleteDirectMessage(userId1, userId2, messageId) {
  const channelId = getDmChannelId(userId1, userId2);
  await updateDoc(doc(db, DM_COLLECTION, channelId, 'messages', messageId), {
    deleted: true,
    deletedAt: new Date().toISOString(),
  });
}

export async function deleteGroupMessage(groupId, messageId) {
  await updateDoc(doc(db, GROUPS_COLLECTION, groupId, 'messages', messageId), {
    deleted: true,
    deletedAt: new Date().toISOString(),
  });
}

export async function getDirectMessages(userId1, userId2, messageLimit = 50) {
  const channelId = getDmChannelId(userId1, userId2);
  const q = query(
    collection(db, DM_COLLECTION, channelId, 'messages'),
    orderBy('createdAt', 'desc'),
    limit(messageLimit)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() })).reverse();
}

export function subscribeToDirectMessages(userId1, userId2, callback, messageLimit = 50) {
  const channelId = getDmChannelId(userId1, userId2);
  const q = query(
    collection(db, DM_COLLECTION, channelId, 'messages'),
    orderBy('createdAt', 'desc'),
    limit(messageLimit)
  );
  return onSnapshot(q, (snapshot) => {
    const messages = snapshot.docs.map(d => ({ id: d.id, ...d.data() })).reverse();
    callback(messages);
  });
}

// --- Read Receipts & Unread Counts ---

export async function markChannelRead(userId, channelId) {
  const docId = `${userId}_${channelId}`;
  await setDoc(doc(db, READ_RECEIPTS_COLLECTION, docId), {
    userId,
    channelId,
    lastReadAt: new Date().toISOString(),
  });
}

export async function getLastReadTime(userId, channelId) {
  const docId = `${userId}_${channelId}`;
  const snap = await getDoc(doc(db, READ_RECEIPTS_COLLECTION, docId));
  return snap.exists() ? snap.data().lastReadAt : null;
}

export async function getUnreadGroupCount(userId, groupId) {
  const lastRead = await getLastReadTime(userId, groupId);
  const q = query(
    collection(db, GROUPS_COLLECTION, groupId, 'messages'),
    orderBy('createdAt', 'desc'),
    limit(50)
  );
  const snap = await getDocs(q);
  if (!lastRead) {
    return snap.docs.filter(d => d.data().userId !== userId).length;
  }
  return snap.docs.filter(d => d.data().createdAt > lastRead && d.data().userId !== userId).length;
}

export async function getUnreadDmCount(userId, otherUserId) {
  const channelId = getDmChannelId(userId, otherUserId);
  const lastRead = await getLastReadTime(userId, channelId);
  const q = query(
    collection(db, DM_COLLECTION, channelId, 'messages'),
    orderBy('createdAt', 'desc'),
    limit(50)
  );
  const snap = await getDocs(q);
  if (!lastRead) {
    return snap.docs.filter(d => d.data().fromUserId === otherUserId).length;
  }
  return snap.docs.filter(d => d.data().createdAt > lastRead && d.data().fromUserId === otherUserId).length;
}

export async function getTotalUnreadCount(userId) {
  let total = 0;

  // Group unreads
  const allGroups = await getGroups();
  for (const group of allGroups) {
    const memberSnap = await getDoc(
      doc(db, GROUPS_COLLECTION, group.id, GROUP_MEMBERS_SUBCOLLECTION, userId)
    );
    if (memberSnap.exists()) {
      const count = await getUnreadGroupCount(userId, group.id);
      total += count;
    }
  }

  // DM unreads
  try {
    const convos = await getDmConversations(userId);
    for (const convo of convos) {
      const otherUserId = convo.participants?.find(id => id !== userId);
      if (otherUserId) {
        const count = await getUnreadDmCount(userId, otherUserId);
        total += count;
      }
    }
  } catch (e) { /* dm_index may not exist */ }

  return total;
}

// --- Group membership, join requests, invite tracking ---

export async function getGroupMembers(groupId) {
  const snap = await getDocs(collection(db, GROUPS_COLLECTION, groupId, GROUP_MEMBERS_SUBCOLLECTION));
  const members = snap.docs.map(d => ({ userId: d.id, ...d.data() }));
  // Attach display names from user profiles
  const withNames = await Promise.all(members.map(async (m) => {
    try {
      const userSnap = await getDoc(doc(db, 'users', m.userId));
      const u = userSnap.exists() ? userSnap.data() : {};
      return {
        ...m,
        // Never fall back to the email address -- member lists are visible to
        // the whole group, and emails are deliberately never surfaced anywhere
        // else in the app either.
        name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || 'UBunited member',
        email: u.email || '',
      };
    } catch (e) {
      return { ...m, name: 'UBunited member', email: '' };
    }
  }));
  // Owner first, then by join date
  return withNames.sort((a, b) => {
    if (a.role === 'owner') return -1;
    if (b.role === 'owner') return 1;
    return (a.joinedAt || '').localeCompare(b.joinedAt || '');
  });
}

export async function requestToJoinGroup(groupId, userId, userName, userEmail) {
  await setDoc(doc(db, GROUPS_COLLECTION, groupId, 'joinRequests', userId), {
    userId,
    userName,
    userEmail,
    status: 'pending',
    requestedAt: new Date().toISOString(),
  });
}

export async function getJoinRequests(groupId) {
  const snap = await getDocs(collection(db, GROUPS_COLLECTION, groupId, 'joinRequests'));
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (b.requestedAt || '').localeCompare(a.requestedAt || ''));
}

export async function getMyJoinRequest(groupId, userId) {
  const snap = await getDoc(doc(db, GROUPS_COLLECTION, groupId, 'joinRequests', userId));
  return snap.exists() ? snap.data() : null;
}

export async function resolveJoinRequest(groupId, requestUserId, approve) {
  if (approve) {
    await joinGroup(groupId, requestUserId);
  }
  await updateDoc(doc(db, GROUPS_COLLECTION, groupId, 'joinRequests', requestUserId), {
    status: approve ? 'approved' : 'denied',
    resolvedAt: new Date().toISOString(),
  });
}

export async function getGroupInvites(groupId) {
  const q = query(collection(db, 'ubunited_group_invites'), where('groupId', '==', groupId));
  const snap = await getDocs(q);
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (b.sentAt || '').localeCompare(a.sentAt || ''));
}

// When a user joins, mark any outstanding invite to their email as joined
export async function markInviteJoined(groupId, userEmail) {
  if (!userEmail) return;
  try {
    const q = query(
      collection(db, 'ubunited_group_invites'),
      where('groupId', '==', groupId),
      where('email', '==', userEmail.toLowerCase())
    );
    const snap = await getDocs(q);
    await Promise.all(snap.docs
      .filter(d => d.data().status === 'sent')
      .map(d => updateDoc(d.ref, { status: 'joined', joinedAt: new Date().toISOString() })));
  } catch (e) { /* non-fatal */ }
}

// --- Message reactions (basic emoji, toggle on/off per user) ---

function toggledReactions(current = {}, emoji, userId) {
  const next = { ...current };
  const users = next[emoji] ? [...next[emoji]] : [];
  const idx = users.indexOf(userId);
  if (idx >= 0) users.splice(idx, 1);
  else users.push(userId);
  if (users.length === 0) delete next[emoji];
  else next[emoji] = users;
  return next;
}

export async function toggleGroupMessageReaction(groupId, messageId, emoji, userId, currentReactions) {
  await updateDoc(doc(db, GROUPS_COLLECTION, groupId, 'messages', messageId), {
    reactions: toggledReactions(currentReactions, emoji, userId),
  });
}

export async function toggleDmMessageReaction(userId1, userId2, messageId, emoji, userId, currentReactions) {
  const channelId = [userId1, userId2].sort().join('_');
  await updateDoc(doc(db, DM_COLLECTION, channelId, 'messages', messageId), {
    reactions: toggledReactions(currentReactions, emoji, userId),
  });
}
